import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, HeartHandshake, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function VolunteerPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [ask, setAsk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [form, setForm] = useState({ full_name: '', mobile: '', password: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/volunteer/${token}`);
      setAsk(r.data);
      setForm(f => ({ ...f, full_name: f.full_name || r.data.parent_name || '' }));
      if (r.data.status === 'accepted') setAccepted(true);
    } catch {
      setAsk(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setAccepting(true);
    try {
      const r = await api.post(`/volunteer/${token}/accept`);
      setAsk(a => ({ ...a, has_account: r.data.has_account }));
      setAccepted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    }
    setAccepting(false);
  };

  const signOutAndRetry = async () => {
    await logout();
    navigate(`/login?redirect=/volunteer/${token}`);
  };

  const createAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/volunteer/${token}/register`, form);
      await checkAuth?.();
      toast.success('Account created');
      navigate('/mentor/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create your account');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="py-20 text-center" data-testid="volunteer-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#F97D6A] mx-auto" />
      </div>
    );
  }

  if (!ask) {
    return (
      <div className="py-20 px-4 text-center" data-testid="volunteer-expired">
        <p className="text-sm text-[#6B7280]">This invitation is no longer available.</p>
      </div>
    );
  }

  const wrongAccount = user && ask.email && user.email?.toLowerCase() !== ask.email.toLowerCase();
  const sameAccount = user && !wrongAccount;

  return (
    <div className="py-10 sm:py-16 px-4" data-testid="volunteer-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl border border-[#E5E7EB] p-8">
        <div className="w-14 h-14 rounded-2xl bg-[#FFF0ED] flex items-center justify-center mb-5">
          {accepted ? <CheckCircle2 className="h-7 w-7 text-emerald-500" /> : <HeartHandshake className="h-7 w-7 text-[#F97D6A]" />}
        </div>

        {!accepted && wrongAccount && (
          <>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-3">
              This invitation belongs to another address
            </h1>
            <p className="text-sm text-[#6B7280] leading-relaxed mb-5" data-testid="volunteer-wrong-account">
              It was sent to {ask.email}, and you are signed in as {user.email}. Sign in with the invited address to
              accept it.
            </p>
            <Button onClick={signOutAndRetry} variant="outline" className="rounded-xl"
              data-testid="volunteer-switch-account-btn">
              Sign out and use {ask.email}
            </Button>
          </>
        )}

        {!accepted && !wrongAccount && (
          <>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-3" data-testid="volunteer-heading">
              Would you talk to students about working as a {ask.career_name}?
            </h1>
            <p className="text-sm text-[#6B7280] leading-relaxed mb-4">
              {ask.school_name || 'Your child\u2019s school'} is looking for someone who could talk to students about
              working as a {ask.career_name}. It is a single session, at a time you choose, about the work you already do.
            </p>
            <p className="text-xs text-[#9CA3AF] leading-relaxed mb-6">
              There is no obligation, and no preparation material is expected. If it is not for you, you can simply
              close this page.
            </p>
            <Button onClick={accept} disabled={accepting}
              className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="volunteer-accept-btn">
              {accepting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Yes, I'd be happy to
            </Button>
          </>
        )}

        {accepted && (
          <>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mb-3" data-testid="volunteer-thanks">
              Thank you — that means a lot
            </h1>
            <p className="text-sm text-[#6B7280] leading-relaxed mb-6">
              {ask.school_name || 'The school'} has been told. The next step is a short mentor profile: what you do,
              and when you are free. Nothing is scheduled until you publish a time yourself.
            </p>

            {wrongAccount && (
              <>
                <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-4 py-3 mb-4" data-testid="volunteer-wrong-account">
                  This invitation was sent to {ask.email}, and you are signed in as {user.email}. Sign in with the
                  invited address to carry on.
                </p>
                <Button onClick={signOutAndRetry} variant="outline" className="rounded-xl"
                  data-testid="volunteer-switch-account-btn">
                  Sign out and use {ask.email}
                </Button>
              </>
            )}

            {sameAccount && (
              <Button onClick={() => navigate('/mentor/onboarding')}
                className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl"
                data-testid="volunteer-continue-signup-btn">
                Continue to mentor signup
              </Button>
            )}

            {!user && ask.has_account && (
              <>
                <p className="text-sm text-[#6B7280] mb-4" data-testid="volunteer-signin-note">
                  You already have a CareersPOV account for {ask.email}. Sign in with it and mentoring will be added to
                  that same account — you will not end up with two logins.
                </p>
                <Button onClick={() => navigate(`/login?redirect=/volunteer/${token}`)}
                  className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl"
                  data-testid="volunteer-signin-btn">
                  Sign in to continue
                </Button>
              </>
            )}

            {!user && !ask.has_account && (
              <form onSubmit={createAccount} className="space-y-4" data-testid="volunteer-signup-form">
                <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl px-4 py-3">
                  <p className="text-xs text-[#6B7280]">
                    Accepting an invitation from <span className="font-semibold text-[#1F2937]">{ask.school_name}</span>
                    {' '}for <span className="font-semibold text-[#1F2937]">{ask.career_name}</span>.
                    {ask.children_to_link > 0 && ' Your child\u2019s records at the school will be added to your account.'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280]">Email</Label>
                  <div className="relative mt-1">
                    <Input value={ask.email} readOnly tabIndex={-1} onChange={() => {}}
                      className="h-10 text-sm bg-[#F3F4F6] text-[#1F2937] font-medium cursor-not-allowed pr-9"
                      data-testid="volunteer-email-locked" />
                    <Lock className="h-3.5 w-3.5 text-[#9CA3AF] absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-[11px] text-[#9CA3AF] mt-1">This is the address the invitation was sent to, so it cannot be changed.</p>
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280]">Your name</Label>
                  <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                    required className="mt-1 h-10 text-sm" data-testid="volunteer-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280]">Mobile</Label>
                  <Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}
                    placeholder="+919876543210" required className="mt-1 h-10 text-sm" data-testid="volunteer-mobile-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280]">Create a password</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 8 characters" required minLength={8}
                    className="mt-1 h-10 text-sm" data-testid="volunteer-password-input" />
                </div>
                <Button type="submit" disabled={saving}
                  className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10"
                  data-testid="volunteer-create-account-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create my account
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
